import axios, { AxiosRequestConfig } from 'axios';
import { GeoJSONPolygon } from 'ol/format/GeoJSON';
import Geometry from 'ol/geom/Geometry';
import { Vector as VectorLayer } from 'ol/layer';
import 'ol/ol.css';
import VectorSource from 'ol/source/Vector';
import {
  microfencesToMicrofenceCluster,
  featuresToLayer,
  LayerResult,
} from '../Components/Map/LiveAndReplay/TypeConversions';
import { FenceZone } from '../util/enums';
import { updateLayerOverlay } from '../Components/Map/Styles/GeofenceStyles';
import { Bounds } from '../Components/Map/types';
import { initialiseMicrofencesOverlay } from '../Components/Map/Styles/MicrofenceStyles';
import { Microfence } from './types/triggers';

// FIX ME: The endpoinst within the API folder should ONLY be fecthing and returning data,
// and NOT setting OpenLayers state or React state/props or similar. (should also be
// grouped by backend service, e.g. triggers, and exported from the api.ts file)

// GETTING LAYERS
export const getVectorLayers = async (
  triggersUrl: string,
  requestConfig: AxiosRequestConfig,
  cid: string,
  pid: string,
  bounds: Bounds,
): Promise<
  { id: string; name: string; source: VectorLayer<VectorSource<Geometry>> }[] | undefined
> => {
  const layers = await axios.get<
    {
      id: string;
      name: string;
      polygons: { id: string; name: string; points: GeoJSONPolygon }[];
      multipolygons: { id: string; name: string; points: GeoJSONPolygon }[];
      lines: { id: string; name: string; points: GeoJSONPolygon }[];
    }[]
  >(
    // Pushed the extentInDegrees to its maximum for now. To speed up the loading.
    `${triggersUrl}/${cid}/${pid}/geofences/withinExtent?latitude=${bounds.latitude}&longitude=${bounds.longitude}&extentInDegrees=${bounds.extentInDegrees}&maxPoints=100`,
    requestConfig,
  );
  const lrs: LayerResult[] = layers.data.map(({ id, name, polygons, multipolygons, lines }) =>
    featuresToLayer(id, name, [
      ...polygons,
      ...multipolygons.map(m => {
        return {
          ...m,
          zone: FenceZone.cleared,
        };
      }),
      ...lines,
    ]),
  );
  return updateLayerOverlay(
    lrs.map(({ lid, name, layer }) => {
      return { id: lid, name, source: layer };
    }),
  );
};

export const getVectorLayer = async (
  triggersUrl: string,
  requestConfig: AxiosRequestConfig,
  cid: string,
  pid: string,
  layerId: string,
  bounds: Bounds,
): Promise<
  { id: string; name: string; source: VectorLayer<VectorSource<Geometry>> } | undefined
> => {
  try {
    const { data: layer } = await axios.get<{
      id: string;
      name: string;
      polygons: { id: string; name: string; points: GeoJSONPolygon }[];
      multipolygons: { id: string; name: string; points: GeoJSONPolygon }[];
      lines: { id: string; name: string; points: GeoJSONPolygon }[];
    }>(
      `${triggersUrl}/${cid}/${pid}/geofences/withinExtent/${layerId}?latitude=${bounds.latitude}&longitude=${bounds.longitude}&extentInDegrees=${bounds.extentInDegrees}&maxPoints=100`,
      requestConfig,
    );
    const lr: LayerResult = featuresToLayer(layerId, layer.name, [
      ...layer.polygons,
      ...layer.multipolygons.map(m => {
        return {
          ...m,
          zone: FenceZone.cleared,
        };
      }),
      ...layer.lines,
    ]);

    return updateLayerOverlay([{ id: lr.lid, name: layer.name, source: lr.layer }])?.find(
      lyr => lyr.id === layerId,
    );
  } catch (error) {
    return;
  }
};

export const getMicrofencesVectorLayer = async (
  triggersUrl: string,
  requestConfig: AxiosRequestConfig,
  cid: string,
  pid: string,
): Promise<VectorLayer<VectorSource<Geometry>>> => {
  // TODO: Should propbably have some kind of 'Simplified' of 'WithinExtent'... Utlra low priority, but kinda defeates the purpose of having pagination in the first place.
  // https://geomoby.atlassian.net/browse/LTP-1484
  const { data: microfences } = await axios.get<Microfence[]>(
    `${triggersUrl}/${cid}/${pid}/microfences`,
    requestConfig,
  );
  return initialiseMicrofencesOverlay(microfencesToMicrofenceCluster(microfences), new Map(), {
    showActivity: false,
  });
};
